/** Native dialogs provide focus containment and restoration, including animated exits. */
export const bindDialog = (dialog, triggers, { closeAnimation = '' } = {}) => {
    const win = dialog?.ownerDocument.defaultView;
    if (!win || !(dialog instanceof win.HTMLDialogElement)) return null;

    let closing = false;
    let revision = 0;

    const updateTriggers = () => {
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(dialog.open)));
    };
    const close = () => {
        if (!dialog.open || closing) return;
        if (!closeAnimation || win.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            dialog.close();
            return;
        }
        closing = true;
        const current = ++revision;
        // A quick close can interrupt entry; continue from the visible position.
        dialog.style.setProperty('--dialog-close-transform', win.getComputedStyle(dialog).transform);
        dialog.classList.add('is-closing');
        const animations = dialog.getAnimations?.().filter((animation) => animation.animationName === closeAnimation) || [];
        if (!animations.length) {
            dialog.close();
            return;
        }
        // Keep the modal, backdrop and scroll lock until the exit actually ends.
        Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
            if (current === revision && dialog.open) dialog.close();
        });
    };

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
            if (!dialog.open) dialog.showModal();
            updateTriggers();
        });
    });
    dialog.querySelectorAll('[data-dialog-close]').forEach((button) => {
        button.addEventListener('click', close);
    });
    if (closeAnimation) {
        dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            close();
        });
    }
    dialog.addEventListener('close', () => {
        revision += 1;
        closing = false;
        dialog.classList.remove('is-closing');
        dialog.style.removeProperty('--dialog-close-transform');
        updateTriggers();
    });
    updateTriggers();

    return { close };
};
